import { useCallback, useEffect, useState } from 'react';

import { ICartEntry } from '@rbi-ctg/menu';
import {
  IBenefitSwap,
  IOffersFragment,
  PromotionType,
  SwapType,
  useLoyaltyUserOffersLazyQuery,
} from 'generated/graphql-gateway';
import { IAppliedOffer, ISwap } from 'generated/rbi-graphql';
import LocalStorage, { StorageKeys } from 'utils/local-storage';
import { parseStringifiedJSON } from 'utils/parse-string';

import { IAppliedRewards, ICartEntryAdapter, isSwap } from './types';

const getAppliedOffersFromStorage = () => {
  const storedAppliedOffers = LocalStorage.getItem(StorageKeys.APPLIED_LOYALTY_OFFERS);
  return parseStringifiedJSON({ value: storedAppliedOffers, defaultValue: [] }) || [];
};

const updateAppliedOffersInStorage = (appliedOffers: IAppliedOffer[]) => {
  LocalStorage.setItem(StorageKeys.APPLIED_LOYALTY_OFFERS, JSON.stringify(appliedOffers));
};

interface IEntriesIdsMap {
  [key: string]: boolean;
}
// Creates a flat map of all cart entries with sanityId property as key
const flattenEntriesToMap: (acc: IEntriesIdsMap, entry: ICartEntryAdapter) => IEntriesIdsMap = (
  acc: IEntriesIdsMap,
  entry: ICartEntryAdapter
) => ({ ...acc, [entry.sanityId]: true, ...entry.children?.reduce(flattenEntriesToMap, {}) });

export const useLoyaltyOffers = (loyaltyId = '') => {
  const [upsizeAvailable, setUpsizeAvailable] = useState(false);
  const [offers, setOffers] = useState<IOffersFragment[]>([]);
  const [appliedOffers, setAppliedOffers] = useState(getAppliedOffersFromStorage);
  const [queryOffers, { data }] = useLoyaltyUserOffersLazyQuery({ fetchPolicy: 'network-only' });
  const [cartEntriesIdsMap, setCartEntriesIdsMap] = useState<IEntriesIdsMap>({});

  useEffect(() => {
    const userOffers = data?.loyaltyUser?.offers;
    if (userOffers?.length) {
      const validOffers = userOffers.map(offer => {
        const isValidSwapOffer = (singleBenefit: IBenefitSwap) => {
          // Checking swap from value is in cartEntries
          const isValidSwap = cartEntriesIdsMap[singleBenefit.value.from];

          if (isValidSwap) {
            setUpsizeAvailable(true);
          }
          return isValidSwap;
        };

        // Filtering valid benefits from offer
        const benefits = offer?.benefits?.filter(benefit => {
          if (isSwap(benefit)) {
            return isValidSwapOffer(benefit);
          }
          // Offer is not a swap so don't filter it
          return true;
        });

        return { ...offer, benefits };
      });

      setOffers(validOffers as IOffersFragment[]);
    }
  }, [data, setOffers, setUpsizeAvailable, cartEntriesIdsMap]);

  const parseOffers = useCallback(
    (appliedLoyaltyOffers: IAppliedOffer[]) =>
      appliedLoyaltyOffers.map(({ cartId: lineId, id, swap }) => ({
        ...(swap && { swap: parseSwap(swap) }),
        id,
        lineId,
        type: PromotionType.OFFER,
      })),
    []
  );

  const parseSwap = ({ cartId: lineId, to, from, swapType }: ISwap) => ({
    to,
    from,
    lineId,
    type: swapType as SwapType,
  });

  const getLoyaltyOffers = useCallback(
    async (cartEntries: ICartEntry[], appliedLoyaltyRewards: IAppliedRewards | null) => {
      // Function maps from ICartEntry to ICartEntryAdapter to send entries to BE
      // Also filters entries that have a reward applied
      const parseEntry = (
        acc: ICartEntryAdapter[],
        { _id, cartId, price = 0, quantity = 1, children }: ICartEntry
      ) => {
        const rewardApplied = appliedLoyaltyRewards?.[cartId];
        if (rewardApplied?.timesApplied === quantity) {
          return acc;
        }

        const parsed: ICartEntryAdapter = {
          sanityId: _id,
          lineId: cartId,
          price,
          quantity,
        };
        if (children.length) {
          parsed.children = children.reduce(parseEntry, []);
        }
        return [...acc, parsed];
      };

      const parsedEntries = cartEntries.reduce(parseEntry, []);
      setCartEntriesIdsMap(parsedEntries.reduce(flattenEntriesToMap, {}));

      queryOffers({
        variables: {
          loyaltyId,
          where: {
            appliedPromotions: parseOffers(appliedOffers),
            cartEntries: parsedEntries,
          },
        },
      });
    },
    [appliedOffers, loyaltyId, queryOffers, parseOffers]
  );

  const resetUpsizeAvailability = () => setUpsizeAvailable(false);

  const resetAppliedOffers = () => {
    updateAppliedOffersInStorage([]);
    setAppliedOffers([]);
  };

  const updateAppliedOffers = (cartEntry: ICartEntry) => {
    setAppliedOffers((oldOffers: IAppliedOffer[]) => {
      const updatedOffers = oldOffers.filter(offer => offer?.cartId !== cartEntry?.cartId);
      updateAppliedOffersInStorage(updatedOffers);

      return updatedOffers;
    });
  };

  const applyOffer = (newOffer: IAppliedOffer) => {
    setAppliedOffers((oldOffers: IAppliedOffer[]) => {
      const updatedOffers = oldOffers.concat(newOffer);
      updateAppliedOffersInStorage(updatedOffers);

      return updatedOffers;
    });
  };

  return {
    appliedOffers,
    getLoyaltyOffers,
    offers,
    resetUpsizeAvailability,
    setAppliedOffers,
    upsizeAvailable,
    applyOffer,
    updateAppliedOffers,
    resetAppliedOffers,
  };
};
